import { logClientError } from '@utils/server-log';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import {
  AUTH_BOOTSTRAP_TIMEOUT_MS,
  AUTH_RELOAD_ATTEMPTS_KEY,
  AuthBootstrapError,
} from '@models/auth-failure';
import { User } from '@models/user';
import { AuthFailureService } from '@services/auth-failure/auth-failure.service';
import { CognitoService } from '@services/cognito/cognito.service';
import { GrpcAuthService } from '@services/grpc-auth/grpc-auth.service';
import { LoadingService } from '@services/loading/loading.service';
import { PermissionsService } from '@services/permissions/permissions.service';
import { SharedService } from '@services/shared/shared.service';
import { SystemService } from '@services/system/system.service';
import { TokenService } from '@services/token/token.service';
import { logPermissionsSnapshot } from '@utils/permissions-debug.utils';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { CookieService } from 'ngx-cookie-service';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

/** Máximo de reloads automáticos após refresh de token (corta loop de recarga). */
const MAX_RELOAD_ATTEMPTS = 2;

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  user?: User;

  constructor(
    private router: Router,
    private shared: SharedService,
    private loadingService: LoadingService,
    private cognitoService: CognitoService,
    private grpcAuthService: GrpcAuthService,
    private cookieService: CookieService,
    private system: SystemService,
    private tokenService: TokenService,
    private permissionsService: PermissionsService,
    private authFailure: AuthFailureService
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Se um logout já está em curso, não reinicia o bootstrap (evita loop).
    if (this.authFailure.isFailing) {
      logClientError('[AuthGuard] Bootstrap abortado: logout em andamento.');
      this.loadingService.loading$.next(false);
      return of(false);
    }

    this.loadingService.loading$.next(true);
    return this.bootstrapAuthGuard(next, state);
  }

  private bootstrapAuthGuard(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    if (this.system.AUTH_PROVIDER === 'cognito') {
      return this.handleCognitoAuth(next, state);
    } else if (this.system.AUTH_PROVIDER === 'svm') {
      return this.handleSvmAuth(next, state);
    } else {
      // Configuração inválida não pode virar tela branca.
      return this.authFailure.fail('unknown_error', {
        error: new Error(`Invalid auth provider: ${this.system.AUTH_PROVIDER}`),
        context: { url: state.url },
      });
    }
  }

  private checkStoredUser() {
    if (!this.shared.accessToken) {
      this.shared.accessToken = this.cookieService.get('accessToken') || null;
    }
    if (!this.shared.refreshToken) {
      this.shared.refreshToken = this.cookieService.get('refreshToken') || null;
    }
    if (!this.shared.user) {
      const userJson = this.cookieService.get('user');
      if (userJson && userJson !== 'null' && userJson !== 'undefined') {
        try {
          this.shared.user = JSON.parse(userJson);
        } catch (e) {
          logClientError('[AuthGuard] Cookie "user" inválido, descartando:', e);
          this.cookieService.delete('user', '/');
        }
      }
    }
  }

  private handleSvmAuth(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const code = next.queryParams['code'];
    const currentState =
      sessionStorage.getItem('oauth_state') || localStorage.getItem('oauth_state');

    // If we have a code in the URL, it means we're returning from OAuth
    if (code) {
      if (!currentState) {
        return this.authFailure.fail('oauth_state_missing', {
          context: { url: state.url, step: 'oauth_callback' },
        });
      }
      return this.handleOAuthCallback(code, currentState, state.url);
    }

    // Check if we have stored credentials
    this.checkStoredUser();

    if (this.shared.accessToken) {
      return from(this.grpcAuthService.getAndSetCurrentUser()).pipe(
        timeout(AUTH_BOOTSTRAP_TIMEOUT_MS),
        catchError((error) => {
          throw new AuthBootstrapError(
            AuthFailureService.resolveReason(error, 'user_fetch_failed'),
            error,
            { step: 'GetAuthUser', url: state.url }
          );
        }),
        switchMap(() => {
          if (!this.shared.user) {
            throw new AuthBootstrapError('user_fetch_failed', undefined, {
              step: 'GetAuthUser',
              detail: 'Resposta sem usuário',
              url: state.url,
            });
          }
          this.user = this.shared.user;
          return this.fetchAndSetUserFeatures(state.url);
        }),
        switchMap(() => this.fetchAndSetUserPermissions(state.url)),
        switchMap(() => this.finalizeBootstrap(next, state.url)),
        catchError((error) => this.failFromBootstrap(error, state.url))
      );
    } else if (this.shared.refreshToken) {
      return from(this.tokenService.refreshAccessToken()).pipe(
        timeout(AUTH_BOOTSTRAP_TIMEOUT_MS),
        switchMap((refreshed) => {
          if (!refreshed) {
            return this.authFailure.fail('token_refresh_failed', {
              context: { step: 'RefreshToken', url: state.url },
            });
          }
          return this.reloadAfterRefresh(state.url);
        }),
        catchError((error) =>
          this.authFailure.fail(AuthFailureService.resolveReason(error, 'token_refresh_failed'), {
            error,
            context: { step: 'RefreshToken', url: state.url },
          })
        )
      );
    } else {
      // Sem credenciais: não é falha, é apenas usuário anônimo.
      return this.redirectToLogin(state.url);
    }
  }

  /**
   * Recarrega a aplicação após renovar o token, com limite de tentativas
   * para nunca cair em loop de reload (tela branca infinita).
   */
  private reloadAfterRefresh(url: string): Observable<boolean> {
    const attempts = Number(sessionStorage.getItem(AUTH_RELOAD_ATTEMPTS_KEY) ?? '0') + 1;

    if (attempts > MAX_RELOAD_ATTEMPTS) {
      sessionStorage.removeItem(AUTH_RELOAD_ATTEMPTS_KEY);
      return this.authFailure.fail('token_refresh_failed', {
        error: new Error(`Reload em loop após refresh (${attempts} tentativas)`),
        context: { step: 'RefreshToken/reload', url, attempts },
      });
    }

    sessionStorage.setItem(AUTH_RELOAD_ATTEMPTS_KEY, String(attempts));
    window.location.reload();
    return of(false);
  }

  /**
   * Decide o acesso final após carregar usuário, menu e permissões.
   *
   * O GetUserPermissions é obrigatório: sem nenhum `resource:read` o bootstrap
   * falha e o usuário é deslogado. O GetUserMenu NÃO serve de fallback.
   */
  private finalizeBootstrap(next: ActivatedRouteSnapshot, url: string): Observable<boolean> {
    if (!this.permissionsService.hasAnyModuleAccess()) {
      throw new AuthBootstrapError('no_permissions', undefined, {
        step: 'finalizeBootstrap',
        url,
        permissions: this.permissionsService.current,
        menuFeatures: this.shared.menuFeatures ?? [],
      });
    }

    this.shared.syncFeaturesFromPermissions();
    logPermissionsSnapshot(this.shared.permissionsDebugSource(), 'bootstrap');

    sessionStorage.removeItem(AUTH_RELOAD_ATTEMPTS_KEY);
    this.updateAuthCookies();
    this.authFailure.resetRedirectTracking();
    return this.loadDataAndNavigate(next);
  }

  /** Converte qualquer exceção do bootstrap em logout + /login com motivo. */
  private failFromBootstrap(error: unknown, url: string): Observable<boolean> {
    return this.authFailure.fail(AuthFailureService.resolveReason(error), {
      error: AuthFailureService.unwrap(error),
      context: { url, ...(AuthFailureService.contextOf(error) ?? {}) },
    });
  }

  private updateAuthCookies() {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 30);

    if (this.shared.accessToken) {
      this.cookieService.set('accessToken', this.shared.accessToken, {
        path: '/',
        expires: expiryDate,
      });
    }
    if (this.shared.refreshToken) {
      this.cookieService.set('refreshToken', this.shared.refreshToken, {
        path: '/',
        expires: expiryDate,
      });
    }
    if (this.shared.user) {
      this.cookieService.set('user', JSON.stringify(this.shared.user), {
        path: '/',
        expires: expiryDate,
      });
    }
  }

  private handleOAuthCallback(code: string, state: string, url: string): Observable<boolean> {
    this.loadingService.loading$.next(true);

    return from(this.grpcAuthService.validateCodeOAuth(code, state)).pipe(
      timeout(AUTH_BOOTSTRAP_TIMEOUT_MS),
      catchError((error) => {
        throw new AuthBootstrapError(
          AuthFailureService.resolveReason(error, 'oauth_failed'),
          error,
          { step: 'ValidateCodeOAuth', url }
        );
      }),
      switchMap((response) => {
        const res = response.toObject();

        if (!res.tokens?.accessToken) {
          throw new AuthBootstrapError('oauth_failed', undefined, {
            step: 'ValidateCodeOAuth',
            detail: 'Resposta sem accessToken',
            url,
          });
        }

        this.shared.accessToken = res.tokens.accessToken;
        this.shared.refreshToken = res.tokens.refreshToken || '';

        sessionStorage.removeItem('oauth_state');
        localStorage.removeItem('oauth_state');

        return from(this.grpcAuthService.getAndSetCurrentUser(res.tokens.accessToken)).pipe(
          timeout(AUTH_BOOTSTRAP_TIMEOUT_MS),
          catchError((error) => {
            throw new AuthBootstrapError(
              AuthFailureService.resolveReason(error, 'user_fetch_failed'),
              error,
              { step: 'GetAuthUser (pós-OAuth)', url }
            );
          })
        );
      }),
      switchMap(() => this.fetchAndSetUserFeatures(url)),
      switchMap(() => this.fetchAndSetUserPermissions(url)),
      switchMap(() => {
        if (!this.permissionsService.hasAnyModuleAccess()) {
          throw new AuthBootstrapError('no_permissions', undefined, {
            step: 'finalizeBootstrap (pós-OAuth)',
            url,
            permissions: this.permissionsService.current,
            menuFeatures: this.shared.menuFeatures ?? [],
          });
        }

        this.shared.syncFeaturesFromPermissions();
        logPermissionsSnapshot(this.shared.permissionsDebugSource(), 'bootstrap pós-OAuth');

        this.updateAuthCookies();
        this.loadingService.loading$.next(false);
        this.authFailure.resetRedirectTracking();

        const homeRoute = this.shared.resolveUserHomeRoute();
        if (!homeRoute) {
          throw new AuthBootstrapError('no_permissions', undefined, {
            step: 'home lookup (pós-OAuth)',
            url,
          });
        }

        this.router.navigateByUrl(homeRoute);
        return of(true);
      }),
      catchError((error) => this.failFromBootstrap(error, url))
    );
  }

  /** Sem credenciais: manda para o login, sem toast de erro. */
  private redirectToLogin(url?: string): Observable<boolean> {
    this.loadingService.loading$.next(false);
    this.router.navigateByUrl('/login');
    return of(false);
  }

  private loadDataAndNavigate(_next: ActivatedRouteSnapshot): Observable<boolean> {
    this.loadingService.loading$.next(false);
    return of(true);
  }

  /**
   * Busca as features/páginas do menu que o usuário tem permissão
   * e armazena no SharedService.
   *
   * Falha aqui NÃO é fatal por si só: o bootstrap segue e a decisão final
   * (`finalizeBootstrap`) desloga caso nem menu nem permissões existam.
   */
  private fetchAndSetUserFeatures(url: string): Observable<boolean> {
    if (this.shared.userFeaturesLoaded && this.shared.userFeatures !== undefined) {
      return of(true);
    }

    return from(this.grpcAuthService.getUserMenu()).pipe(
      timeout(AUTH_BOOTSTRAP_TIMEOUT_MS),
      map((response) => {
        const res = response.toObject();
        const features = res.menu || [];
        this.shared.menuFeatures = features;
        this.shared.userFeatures = features;
        this.shared.userFeatures$.next(features);
        this.shared.userFeaturesLoaded = true;
        return true;
      }),
      catchError((error) => {
        logClientError('[AuthGuard] Erro em GetUserMenu:', { url, error });
        this.shared.menuFeatures = [];
        this.shared.userFeatures = [];
        this.shared.userFeatures$.next([]);
        this.shared.userFeaturesLoaded = true;
        return of(false);
      })
    );
  }

  /**
   * Loads fine-grained permissions (`resource:action`) once per session bootstrap.
   *
   * Qualquer erro aqui é fatal: sem permissões confiáveis a navegação fica
   * inconsistente (guards negam tudo e a home devolve para a mesma rota),
   * então deslogamos com o motivo em vez de arrastar o usuário para um loop.
   */
  private fetchAndSetUserPermissions(url: string): Observable<boolean> {
    if (this.permissionsService.fetchedFromApi) {
      // Só é buscado UMA vez por sessão do SPA. Em navegações seguintes (ou
      // após HMR do ng serve, que não reinicia os singletons) o RPC é pulado.
      return of(true);
    }


    return from(this.grpcAuthService.getUserPermissions()).pipe(
      timeout(AUTH_BOOTSTRAP_TIMEOUT_MS),
      map((response) => {
        const res = response.toObject();
        // Backend omits the field when empty — treat absence as [].
        const permissions = res.permissions ?? [];
        this.permissionsService.setPermissions(permissions);
        return true;
      }),
      catchError((error) => {
        logClientError('[AuthGuard] Erro em GetUserPermissions:', { url, error });
        this.permissionsService.clear();
        throw new AuthBootstrapError(
          AuthFailureService.resolveReason(error, 'permissions_fetch_failed'),
          error,
          { step: 'GetUserPermissions', url }
        );
      })
    );
  }

  // Cognito Auth (keeping existing implementation)
  private handleCognitoAuth(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    const cognitoUser = this.cognitoService.getCurrentUser();

    if (cognitoUser) {
      return from(
        new Promise<boolean>((resolve, reject) => {
          cognitoUser.getSession((err: Error | null, session: null | CognitoUserSession) => {
            if (err) {
              reject(err);
            } else if (session?.isValid()) {
              cognitoUser.getUserAttributes((attrError: any, attributes: any) => {
                if (attrError) {
                  this.cognitoService
                    .fetchUserAttributes(session.getAccessToken().getJwtToken())
                    .then((user) => {
                      this.user = user as User | undefined;
                      resolve(true);
                    })
                    .catch((fetchError) => reject(fetchError));
                } else {
                  const user: User = this.cognitoService.getUserFromCognito(
                    cognitoUser,
                    session,
                    attributes
                  );
                  this.user = user;
                  this.shared.user$.next({ ...user });
                  resolve(true);
                }
              });
            } else {
              logClientError('[AuthGuard] Sessão Cognito inválida');
              resolve(false);
            }
          });
        })
      ).pipe(
        timeout(AUTH_BOOTSTRAP_TIMEOUT_MS),
        switchMap((isSessionValid: boolean) => {
          if (isSessionValid) {
            return this.loadDataAndNavigate(next);
          }
          return this.authFailure.fail('session_expired', {
            context: { step: 'Cognito getSession', url: state.url },
          });
        }),
        catchError((error) =>
          this.authFailure.fail(AuthFailureService.resolveReason(error, 'session_expired'), {
            error,
            context: { step: 'Cognito getSession', url: state.url },
          })
        )
      );
    }

    const code = next.queryParams['code'];

    if (code) {
      return from(this.cognitoService.exchangeCodeForToken(code)).pipe(
        timeout(AUTH_BOOTSTRAP_TIMEOUT_MS),
        switchMap(() => {
          this.router.navigateByUrl(`/`);
          return of(false);
        }),
        catchError((error) =>
          this.authFailure.fail(AuthFailureService.resolveReason(error, 'oauth_failed'), {
            error,
            context: { step: 'Cognito exchangeCodeForToken', url: state.url },
          })
        )
      );
    }

    return this.redirectToLogin(state.url);
  }
}
